import {DestroyRef, Injectable, computed, inject, signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BzTableColumnSettings} from '@bringoz/ui';
import {EMPTY, Observable, Subject, forkJoin, of, timer} from 'rxjs';
import {catchError, expand, reduce, switchMap, takeUntil} from 'rxjs/operators';
import * as moment from 'moment';
import * as _ from 'lodash';
import {TimeCardModel} from '../models/timecard.model';
import {ModelListWithCursor} from '../../../commons/angular/models/model-list-with-cursor.model';
import {TimeCardGroup} from '../models/timecard-group.model';
import {
  CompleteTimecardsQueryModel,
  GetTimeCardGroupsQueryModel,
  TimekeepingHttpService
} from '../services/timekeeping-http.service';
import {TimeCardTotalsModel} from '../../models/timecard-totals.model';
import {TimeCardStatusType} from '../../types/timecard-status.type';
import {TimeCardDisplayStatusGroupType} from '../../types/timecard-display-status-group.type';
import {SiteFiltersType} from '../../../commons/types/site-filters.type';
import {ProcessTrackingStatusEnum} from '../../../commons/types/process-tracking-status.enum';
import {ProcessTrackingModel} from '../../../commons/models/process-tracking.model';
import {ProcessTrackingTypeEnum} from '../../../commons/types/process-tracking-type.enum';
import {BatchTypeEnum} from '../../../batch/enums/batch-type.enum';
import {BatchProcessTrackingModel} from '../../../batch/models/batch-process-tracking.model';
import {TimekeepingExportPayrollPayloadModel} from '../../models/timekeeping-export-payroll-payload.model';
import {TIME_CARD_BULK_PROCESS_IN_PROGRESS, TIME_CARDS_NOT_FOUND} from '../constants/timekeeping-error-codes.constant';
import {injectLegacy} from '../../../commons/angular/utils/inject-legacy.util';
import {GroupsHttpService} from '../../../groups/angular/services/groups-http.service';
import {GroupModel} from '../../../groups/models/group.model';
import {BranchesHttpService} from '../../../branches/angular/services/branches-http.service';
import {BranchModel} from '../../../branches/models/branch.model';
import {TagsHttpService} from '../../../tags/angular/services/tags-http.service';
import {TagModel} from '../../../tags/angular/models/tag.model';
import {TagType} from '../../../tags/angular/types/tag.type';
import type {BringozToastService} from '../../../commons/services/bringoz-toast.service';
import type {ViewFilterSettingsService} from '../../../commons/services/view-filter-settings.service';
import {
  TimekeepingDateRange,
  TimekeepingFilterOption,
  TimekeepingListFilters,
  TimekeepingRouteParams,
  TimekeepingStickyFilters,
  TimekeepingTotalsCard,
  TimekeepingViewSettings
} from '../models/timekeeping-list.model';

/** Everything the inline toolbar filters choose from, loaded once with the screen. */
export interface TimekeepingFilterSources {
  agentTags: TagModel[];
  groupTags: TagModel[];
  groups: GroupModel[];
  branches: BranchModel[];
}

export interface TimekeepingListState {
  pageName: string;
  range: TimekeepingDateRange;
  filters: TimekeepingListFilters;
  filterSources: TimekeepingFilterSources;
  filterSourcesLoaded: boolean;
  searchQuery: string;
  timeCardGroups: TimeCardGroup[];
  cursor: string | null;
  hasMoreData: boolean;
  loading: boolean;
  refreshing: boolean;
  totals: TimeCardTotalsModel;
  selectedTimecardIds: string[];
  completing: boolean;
  exporting: boolean;
  exportingPayroll: boolean;
  settings: TimekeepingViewSettings;
}

export interface TimekeepingApprovableSelection {
  approvedIds: string[];
  skippedCount: number;
}

type TimekeepingListStateUpdate = Partial<TimekeepingListState> | ((state: TimekeepingListState) => Partial<TimekeepingListState>);

const STICKY_FILTER_KEYS: (keyof TimekeepingStickyFilters)[] = ['groupTags', 'groupIds', 'branchIds', 'tags'];

export const TIMEKEEPING_LOAD_LIMIT = 20;
const COMPLETE_POLLING_INTERVAL_MS = 3000;
const FILTER_SOURCES_PAGE_LIMIT = 1000;
const BROWSE_DAYS = 7;

const TOTALS_CARDS_CONFIG: { status: TimeCardDisplayStatusGroupType; totalsKey: keyof TimeCardTotalsModel; badgeClass?: string }[] = [
  {status: TimeCardDisplayStatusGroupType.total, totalsKey: 'total'},
  {status: TimeCardDisplayStatusGroupType.open, totalsKey: 'open', badgeClass: 'totals-badge-Open'},
  {status: TimeCardDisplayStatusGroupType.closed, totalsKey: 'closed', badgeClass: 'totals-badge-Closed'},
  {status: TimeCardDisplayStatusGroupType.acknowledged, totalsKey: 'acknowledged', badgeClass: 'totals-badge-Acknowledged'},
  {status: TimeCardDisplayStatusGroupType.approved, totalsKey: 'approved', badgeClass: 'totals-badge-Approved'},
  {status: TimeCardDisplayStatusGroupType.completed, totalsKey: 'completed', badgeClass: 'totals-badge-Completed'},
  {status: TimeCardDisplayStatusGroupType.archived, totalsKey: 'archived', badgeClass: 'totals-badge-Archived'}
];

const EMPTY_TOTALS: TimeCardTotalsModel = {
  total: 0, open: 0, closed: 0, acknowledged: 0,
  approved: 0, completed: 0, archived: 0, infeasible: 0, warning: 0
};

const EMPTY_FILTER_SOURCES: TimekeepingFilterSources = {agentTags: [], groupTags: [], groups: [], branches: []};

/** The external id is what users search by, so it is part of the label unless the name already carries it. */
function toFilterOption(id: string, name: string, externalId?: string): TimekeepingFilterOption {
  const label = externalId && !_.includes(name, externalId) ? `${name} [${externalId}]` : name;
  return {id, label: label || id};
}

function sortFilterOptions(options: TimekeepingFilterOption[]): TimekeepingFilterOption[] {
  return _.sortBy(options, (option) => _.toLower(option.label));
}

/** A tag filter selects by tag name, which is what the API filters by. */
function toTagFilterOptions(tags: TagModel[]): TimekeepingFilterOption[] {
  return sortFilterOptions(_.map(tags, (tag) => toFilterOption(tag.name, tag.name)));
}

/** Every page of a cursor list, merged. */
function loadAllPages<T>(loadPage: (cursor?: string) => Observable<ModelListWithCursor<T>>): Observable<T[]> {
  return loadPage().pipe(
    expand((response) => response?.cursor && response.list?.length ? loadPage(response.cursor) : EMPTY),
    reduce((items, response) => items.concat(response?.list || []), [] as T[])
  );
}

/** Bulk complete and payroll export share one lock per site and refuse with the same codes. */
function bulkProcessErrorToast(error: HttpErrorResponse, body: string): { body: string; details?: string } {
  switch (error?.error?.code) {
    case TIME_CARD_BULK_PROCESS_IN_PROGRESS:
      return {body: 'TIMEKEEPING_BULK_PROCESS_IN_PROGRESS'};
    case TIME_CARDS_NOT_FOUND:
      return {body: 'TIMEKEEPING_TIMECARDS_NOT_FOUND'};
    default:
      return {body, details: error?.message || 'SOMETHING_WENT_WRONG'};
  }
}

function isPayrollExport(process: ProcessTrackingModel | null): boolean {
  return process?.processType === ProcessTrackingTypeEnum.BatchProcess
    && (process as BatchProcessTrackingModel).batchType === BatchTypeEnum.ExportTimeCardsPayroll;
}

const initialState: TimekeepingListState = {
  pageName: undefined,
  range: undefined,
  filters: {
    groupTags: [],
    groupIds: [],
    branchIds: [],
    tags: [],
    statuses: [],
    displayStatusGroups: [TimeCardDisplayStatusGroupType.total]
  },
  filterSources: EMPTY_FILTER_SOURCES,
  filterSourcesLoaded: false,
  searchQuery: '',
  timeCardGroups: [],
  cursor: null,
  hasMoreData: true,
  loading: false,
  refreshing: false,
  totals: EMPTY_TOTALS,
  selectedTimecardIds: [],
  completing: false,
  exporting: false,
  exportingPayroll: false,
  settings: undefined
};

/**
 * State of the timecards list screen. Provided by the screen container, so it lives and dies with the screen.
 */
@Injectable()
export class TimekeepingListStore {

  private readonly timekeepingHttpService = inject(TimekeepingHttpService);
  private readonly groupsHttpService = inject(GroupsHttpService);
  private readonly branchesHttpService = inject(BranchesHttpService);
  private readonly tagsHttpService = inject(TagsHttpService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly bringozToastService = injectLegacy<BringozToastService>('bringozToastService');
  private readonly viewFilterSettingsService = injectLegacy<ViewFilterSettingsService>('viewFilterSettingsService');

  private readonly state = signal<TimekeepingListState>(initialState);

  // Selectors
  readonly pageName = computed(() => this.state().pageName);
  readonly range = computed(() => this.state().range);
  readonly filters = computed(() => this.state().filters);
  readonly searchQuery = computed(() => this.state().searchQuery);
  readonly timeCardGroups = computed(() => this.state().timeCardGroups);
  readonly hasMoreData = computed(() => this.state().hasMoreData);
  readonly loading = computed(() => this.state().loading);
  readonly exporting = computed(() => this.state().exporting);
  readonly exportingPayroll = computed(() => this.state().exportingPayroll);
  readonly settings = computed(() => this.state().settings);
  readonly columns = computed(() => this.settings()?.columns);
  readonly selectedStatuses = computed(() => this.filters().statuses);
  readonly isInitialLoading = computed(() => this.loading() && !this.timeCardGroups().length);
  readonly showRefreshOverlay = computed(() => this.state().refreshing && this.timeCardGroups().length > 0);
  readonly showTable = computed(() => !this.loading() || this.timeCardGroups().length > 0);
  readonly completeDisabled = computed(() => !this.state().selectedTimecardIds.length || this.state().completing);
  private readonly totals = computed(() => this.state().totals);

  // The bz-table column manager writes into what it is given, so it gets its own copy instead of the store state.
  readonly tableSettings = computed<BzTableColumnSettings>(() => _.cloneDeep(this.settings()?.table));

  // Inline filters cascade like the scheduling project table: Region / Market selects its teams, and Teams select
  // their territories. Each level also narrows the options below it. Employee Role stands apart from that hierarchy.
  private readonly filterSources = computed(() => this.state().filterSources);

  readonly agentTagOptions = computed(() => toTagFilterOptions(this.filterSources().agentTags));
  readonly groupTagOptions = computed(() => toTagFilterOptions(this.filterSources().groupTags));

  private readonly regionGroups = computed<GroupModel[]>(() => {
    const groupTags = this.filters().groupTags;
    const groups = this.filterSources().groups;
    return groupTags?.length ? _.filter(groups, (group) => !_.isEmpty(_.intersection(group.tags, groupTags))) : groups;
  });

  readonly groupOptions = computed(() => sortFilterOptions(
    _.map(this.regionGroups(), (group) => toFilterOption(group.id, group.name, group.externalId))
  ));

  /** Territories of the selected teams, else of every team in the selected regions. Undefined when neither is selected. */
  private readonly hierarchyBranchIds = computed<string[] | undefined>(() => {
    const {groupTags, groupIds} = this.filters();
    if (groupIds?.length) {
      return this.groupsBranchIds(groupIds);
    }
    if (groupTags?.length) {
      return _.uniq(_.flatMap(this.regionGroups(), (group) => group.branchIds || []));
    }
    return undefined;
  });

  readonly branchOptions = computed(() => {
    const hierarchyBranchIds = this.hierarchyBranchIds();
    const branches = this.filterSources().branches;
    const hierarchyBranchKeys = hierarchyBranchIds && new Set(hierarchyBranchIds);
    const visibleBranches = hierarchyBranchKeys ? _.filter(branches, (branch) => hierarchyBranchKeys.has(branch.id)) : branches;
    return sortFilterOptions(_.map(visibleBranches, (branch) => toFilterOption(branch.id, branch.name, branch.externalId)));
  });

  /** What the list is filtered by: the selected territories, else every territory the levels above narrow to. */
  readonly queryBranchIds = computed<string[]>(() => {
    const branchIds = this.filters().branchIds;
    return branchIds?.length ? branchIds : (this.hierarchyBranchIds() || []);
  });

  /** A region or team without territories matches no timecard, but an empty branchIds would not filter at all. */
  private readonly hierarchyMatchesNothing = computed(() =>
    this.state().filterSourcesLoaded && !!this.hierarchyBranchIds() && !this.queryBranchIds().length
  );

  readonly totalsCards = computed<TimekeepingTotalsCard[]>(() => {
    const displayGroups = this.filters().displayStatusGroups;
    const totals = this.totals();
    const isTotal = !displayGroups?.length || _.includes(displayGroups, TimeCardDisplayStatusGroupType.total);
    return _.map(TOTALS_CARDS_CONFIG, (config) => ({
      status: config.status,
      labelKey: `TIMECARD_TOTALS.${config.totalsKey}`,
      badgeClass: config.badgeClass,
      value: totals?.[config.totalsKey] ?? 0,
      selected: config.status === TimeCardDisplayStatusGroupType.total ? isTotal : _.includes(displayGroups, config.status)
    }));
  });

  /** URL state params of the list, kept in sync by the container. */
  // Like the scheduling project, the toolbar filters live in local storage and not in the URL: a region can hold
  // hundreds of territories. null drops the ones a link brought in, which init has already saved.
  readonly urlParams = computed<Record<string, unknown>>(() => {
    const range = this.range();
    const filters = this.filters();
    return {
      from: range?.dateStart,
      to: range?.dateEnd,
      groupTags: null,
      groupIds: null,
      branchIds: null,
      tags: null,
      statuses: filters.statuses,
      displayStatusGroups: filters[SiteFiltersType.displayStatusGroups],
      query: this.searchQuery() || null
    };
  });

  private readonly loadGroups$ = new Subject<void>();
  private readonly fetchNext$ = new Subject<string>();
  private readonly loadTotals$ = new Subject<void>();
  private readonly complete$ = new Subject<string[]>();
  private readonly exportPayroll$ = new Subject<TimekeepingExportPayrollPayloadModel>();
  private readonly pollStop$ = new Subject<void>();
  private readonly payrollExportSubject$ = new Subject<BatchProcessTrackingModel>();

  /** A running payroll export, followed in the processing dialog until it ends. */
  readonly payrollExport$ = this.payrollExportSubject$.asObservable();

  constructor() {
    // switchMap: a newer request cancels the one still in flight, so a stale response never overwrites the list.
    this.loadGroups$.pipe(
      switchMap(() => (this.hierarchyMatchesNothing()
        ? of<ModelListWithCursor<TimeCardGroup>>({list: [], cursor: null})
        : this.timekeepingHttpService.getTimeCardGroups(this.buildGroupsQuery())).pipe(
        catchError((error) => {
          console.error('Error loading timecard groups:', error);
          this.patch({timeCardGroups: [], selectedTimecardIds: [], loading: false, refreshing: false});
          return EMPTY;
        })
      )),
      takeUntilDestroyed()
    ).subscribe((response) => this.patch({
      // The table drops its selection whenever it gets a new list, so the store does too.
      timeCardGroups: response?.list || [],
      selectedTimecardIds: [],
      cursor: response?.cursor,
      hasMoreData: !!response?.cursor,
      loading: false,
      refreshing: false
    }));

    this.fetchNext$.pipe(
      switchMap((cursor) => this.timekeepingHttpService.getTimeCardGroups({...this.buildGroupsQuery(), cursor}).pipe(
        catchError((error) => {
          console.error('Error loading more timecard groups:', error);
          this.patch({loading: false});
          return EMPTY;
        }),
        // A reload replaces the list, so a page still in flight must not be appended to it.
        takeUntil(this.loadGroups$)
      )),
      takeUntilDestroyed()
    ).subscribe((response) => this.patch((state) => {
      const hasNewGroups = !!response?.list?.length;
      return {
        timeCardGroups: hasNewGroups ? state.timeCardGroups.concat(response.list) : state.timeCardGroups,
        selectedTimecardIds: hasNewGroups ? [] : state.selectedTimecardIds,
        cursor: response?.cursor || null,
        hasMoreData: !!response?.cursor,
        loading: false
      };
    }));

    this.loadTotals$.pipe(
      switchMap(() => (this.hierarchyMatchesNothing()
        ? of(EMPTY_TOTALS)
        : this.timekeepingHttpService.getTimeCardTotals(this.buildTotalsQuery())).pipe(
        catchError((error) => {
          console.error('Error loading timecard totals:', error);
          return EMPTY;
        })
      )),
      takeUntilDestroyed()
    ).subscribe((totals) => this.patch({totals}));

    this.complete$.pipe(
      switchMap((ids) => this.timekeepingHttpService.completeTimecards(this.buildCompleteQuery(ids)).pipe(
        catchError((error) => {
          console.error('Error completing timecards:', error);
          this.patch({completing: false});
          this.bringozToastService.error(bulkProcessErrorToast(error, 'TIMECARD_COMPLETE_ERROR'));
          return EMPTY;
        })
      )),
      takeUntilDestroyed()
    ).subscribe(() => {
      this.bringozToastService.success({body: 'TIMECARD_COMPLETE_STARTED'});
      this.pollBulkProcess();
    });

    this.exportPayroll$.pipe(
      switchMap((payload) => this.timekeepingHttpService.exportPayroll(payload).pipe(
        catchError((error) => {
          console.error('Error exporting payroll:', error);
          this.patch({exportingPayroll: false});
          this.bringozToastService.error(bulkProcessErrorToast(error, 'COMMONS_EXPORT_TO_MAIL_ERROR'));
          return EMPTY;
        })
      )),
      takeUntilDestroyed()
    ).subscribe(() => this.pollBulkProcess());
  }

  // ******************************
  //        Init
  // ******************************

  init(routeParams: TimekeepingRouteParams, pageName: string, settings: TimekeepingViewSettings) {
    // A link carries its own filters and they become the user's last ones; otherwise the last ones are restored.
    const hasRouteStickyFilters = _.some(STICKY_FILTER_KEYS, (key) => !_.isEmpty(routeParams[key]));
    const stickyFilters: Partial<TimekeepingStickyFilters> = hasRouteStickyFilters
      ? routeParams
      : this.viewFilterSettingsService.getPageFilters<TimekeepingStickyFilters>(pageName) || {};

    this.patch({
      pageName,
      settings,
      searchQuery: routeParams.queryString || '',
      range: {
        dateStart: routeParams.from || moment().startOf('week').valueOf(),
        dateEnd: routeParams.to || moment().endOf('week').valueOf()
      },
      filters: {
        groupTags: stickyFilters.groupTags || [],
        groupIds: stickyFilters.groupIds || [],
        branchIds: stickyFilters.branchIds || [],
        tags: stickyFilters.tags || [],
        statuses: routeParams.statuses || [],
        displayStatusGroups: routeParams.displayStatusGroups?.length
          ? routeParams.displayStatusGroups
          : [TimeCardDisplayStatusGroupType.total]
      }
    });

    if (hasRouteStickyFilters) {
      this.saveStickyFilters();
    }

    // Teams and Region / Market filter the list through their territories, so the first load waits for them.
    const {groupTags, groupIds, branchIds} = this.state().filters;
    const waitsForFilterSources = !branchIds.length && !!(groupTags.length || groupIds.length);

    this.loadFilterSources()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filterSources) => {
        this.patch({filterSources, filterSourcesLoaded: true});
        if (waitsForFilterSources) {
          this.loadGroups(true);
        }
      });

    if (!waitsForFilterSources) {
      this.loadGroups(true);
    }

    // A payroll export keeps the site locked until it ends or is recovered, so one started earlier is picked up again.
    this.timekeepingHttpService.getBulkProcess()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (isPayrollExport(response)) {
            this.patch({exportingPayroll: true});
            this.pollBulkProcess();
          }
        },
        error: (error) => console.error('Error loading bulk process:', error)
      });
  }

  // ******************************
  //        Filters
  // ******************************

  setStatuses(statuses: string[]) {
    this.patch((state) => ({
      filters: {
        ...state.filters,
        statuses: statuses || [],
        displayStatusGroups: [TimeCardDisplayStatusGroupType.total]
      }
    }));
    this.loadGroups(true);
  }

  setAgentTags(tags: string[]) {
    this.patchFilters({tags: tags || []});
    this.onStickyFiltersChange();
  }

  /** Selects every team of the selected regions and their territories; clearing the regions clears both. */
  setGroupTags(groupTags: string[]) {
    this.patchFilters({groupTags: groupTags || []});
    const groupIds = groupTags?.length ? _.map(this.regionGroups(), 'id') : [];
    this.patchFilters({groupIds, branchIds: this.groupsBranchIds(groupIds)});
    this.onStickyFiltersChange();
  }

  /** Selects every territory of the selected teams; clearing the teams clears the territories. */
  setGroupIds(groupIds: string[]) {
    const selectedGroupIds = groupIds || [];
    this.patchFilters({groupIds: selectedGroupIds, branchIds: this.groupsBranchIds(selectedGroupIds)});
    this.onStickyFiltersChange();
  }

  setBranchIds(branchIds: string[]) {
    this.patchFilters({branchIds: branchIds || []});
    this.onStickyFiltersChange();
  }

  setRange(range: TimekeepingDateRange) {
    this.patch({range: {dateStart: range.dateStart, dateEnd: range.dateEnd}});
    this.loadGroups(true);
  }

  browseWeek(isNext: boolean) {
    const range = this.state().range;
    const days = isNext ? BROWSE_DAYS : -BROWSE_DAYS;
    this.setRange({
      dateStart: moment(range.dateStart).add(days, 'days').valueOf(),
      dateEnd: moment(range.dateEnd).add(days, 'days').valueOf()
    });
  }

  setSearchQuery(query: string) {
    this.patch({searchQuery: query || ''});
    this.loadGroups(true);
  }

  toggleTotalsStatus(status: TimeCardDisplayStatusGroupType) {
    const displayGroups: string[] = this.state().filters.displayStatusGroups || [];
    let nextGroups: string[];

    if (status === TimeCardDisplayStatusGroupType.total) {
      if (_.includes(displayGroups, TimeCardDisplayStatusGroupType.total)) {
        return;
      }
      nextGroups = [TimeCardDisplayStatusGroupType.total];
    } else {
      nextGroups = _.includes(displayGroups, status) ? _.without(displayGroups, status) : [...displayGroups, status];
    }

    if (!nextGroups.length) {
      nextGroups = [TimeCardDisplayStatusGroupType.total];
    } else if (nextGroups.length > 1 && _.includes(nextGroups, TimeCardDisplayStatusGroupType.total)) {
      nextGroups = _.without(nextGroups, TimeCardDisplayStatusGroupType.total);
    }

    this.patch((state) => ({
      filters: {...state.filters, displayStatusGroups: nextGroups, statuses: []}
    }));
    // Totals do not depend on the display groups, so they are not reloaded here.
    this.loadGroups(false);
  }

  // ******************************
  //        Selection & settings
  // ******************************

  setSelection(selectedIds: string[]) {
    this.patch({selectedTimecardIds: selectedIds || []});
  }

  getApprovableSelection(): TimekeepingApprovableSelection {
    const state = this.state();
    const selectedSet = new Set(state.selectedTimecardIds);
    const selectedTimecards: TimeCardModel[] = _.filter(
      _.flatMap(state.timeCardGroups, (group) => group.timeCards || []),
      (timecard) => selectedSet.has(timecard.id)
    );
    const approved = _.filter(selectedTimecards, (timecard) => timecard.status === TimeCardStatusType.Approved);

    return {
      approvedIds: _.map(approved, 'id'),
      skippedCount: selectedTimecards.length - approved.length
    };
  }

  setSettings(settings: TimekeepingViewSettings) {
    this.patch({settings});
  }

  setTableSettings(table: BzTableColumnSettings) {
    this.patch((state) => ({settings: {...state.settings, table}}));
  }

  setExporting(exporting: boolean) {
    this.patch({exporting});
  }

  // ******************************
  //        Loading
  // ******************************

  loadGroups(refreshTotals: boolean) {
    this.patch({cursor: null, loading: true, refreshing: true});
    this.loadGroups$.next();

    if (refreshTotals) {
      this.loadTotals$.next();
    }
  }

  fetchNext() {
    const {cursor, loading} = this.state();
    if (!cursor || loading) {
      return;
    }
    this.patch({loading: true});
    this.fetchNext$.next(cursor);
  }

  // ******************************
  //        Complete
  // ******************************

  complete(ids: string[]) {
    this.patch({completing: true});
    this.complete$.next(ids);
  }

  // ******************************
  //        Payroll export
  // ******************************

  exportPayroll(payload: TimekeepingExportPayrollPayloadModel) {
    this.patch({exportingPayroll: true});
    this.exportPayroll$.next(payload);
  }

  /** An export that ended is polled once more for its result; Cancel, or closing without a choice, stops following it. */
  resolvePayrollExport(following?: boolean) {
    if (following) {
      this.pollBulkProcess();
      return;
    }
    this.patch({exportingPayroll: false});
  }

  /** Bulk complete and payroll export never run together on a site, so one poll follows whichever was started. */
  private pollBulkProcess() {
    // Cancel any prior poll (and any in-flight HTTP from it) before starting a new one.
    this.pollStop$.next();

    timer(0, COMPLETE_POLLING_INTERVAL_MS)
      .pipe(
        // switchMap: a new tick cancels the previous in-flight HTTP, preventing pile-up.
        switchMap(() => this.timekeepingHttpService.getBulkProcess()),
        takeUntil(this.pollStop$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response: ProcessTrackingModel | null) => {
          if (isPayrollExport(response)) {
            this.pollStop$.next();
            this.payrollExportSubject$.next(response as BatchProcessTrackingModel);
          } else if (!response || response.processStatus === ProcessTrackingStatusEnum.COMPLETED) {
            this.pollStop$.next();
            if (this.state().completing) {
              this.patch({completing: false, selectedTimecardIds: []});
              this.bringozToastService.success({body: 'TIMECARD_COMPLETE_SUCCESS'});
            } else {
              this.patch({exportingPayroll: false});
              this.bringozToastService.success({body: 'COMMONS_EXPORT_TO_MAIL_SUCCESS'});
            }
            this.loadGroups(true);
          } else if (response.processStatus === ProcessTrackingStatusEnum.FAIL) {
            this.pollStop$.next();
            const errorDetails = response.errors?.length > 0
              ? _.map(response.errors, (e) => e?.message || e).join(', ')
              : 'SOMETHING_WENT_WRONG';
            if (this.state().completing) {
              this.patch({completing: false});
              this.bringozToastService.error({
                body: 'TIMECARD_COMPLETE_ERROR',
                details: errorDetails
              });
            } else {
              this.patch({exportingPayroll: false});
              this.bringozToastService.error({
                body: 'COMMONS_EXPORT_TO_MAIL_ERROR',
                details: errorDetails
              });
            }
          }
          // If still IN_PROGRESS, continue polling
        },
        error: (error) => {
          console.error('Error polling bulk process status:', error);
          this.pollStop$.next();
          this.patch({completing: false, exportingPayroll: false});
        }
      });
  }

  // ******************************
  //        Queries
  // ******************************

  private buildGroupsQuery(): GetTimeCardGroupsQueryModel {
    const {range, filters, searchQuery} = this.state();
    const queryParams: GetTimeCardGroupsQueryModel = {
      from: range.dateStart,
      to: range.dateEnd,
      returnTimeCards: true,
      limit: TIMEKEEPING_LOAD_LIMIT
    };

    const branchIds = this.queryBranchIds();
    if (branchIds.length) {
      queryParams.branchIds = branchIds;
    }

    if (filters.tags?.length) {
      queryParams.tags = filters.tags;
    }

    const displayGroups: string[] = filters.displayStatusGroups || [];
    const isTotalOnly = displayGroups.length === 1 && displayGroups[0] === TimeCardDisplayStatusGroupType.total;

    if (!isTotalOnly && displayGroups.length) {
      const statusValues = displayGroups.filter(s => s !== TimeCardDisplayStatusGroupType.total);
      if (statusValues.length) {
        queryParams.statuses = statusValues;
      }
    } else if (filters.statuses?.length) {
      queryParams.statuses = filters.statuses;
    }

    if (searchQuery) {
      queryParams.query = searchQuery;
    }

    return queryParams;
  }

  private buildTotalsQuery(): GetTimeCardGroupsQueryModel {
    const {range, filters, searchQuery} = this.state();
    const queryParams: GetTimeCardGroupsQueryModel = {
      from: range.dateStart,
      to: range.dateEnd
    };

    const branchIds = this.queryBranchIds();
    if (branchIds.length) {
      queryParams.branchIds = branchIds;
    }

    if (filters.tags?.length) {
      queryParams.tags = filters.tags;
    }

    if (searchQuery) {
      queryParams.query = searchQuery;
    }

    return queryParams;
  }

  private buildCompleteQuery(ids: string[]): CompleteTimecardsQueryModel {
    const {range, searchQuery} = this.state();
    const queryParams: CompleteTimecardsQueryModel = {
      from: range.dateStart,
      to: range.dateEnd
    };

    const branchIds = this.queryBranchIds();
    if (branchIds.length) {
      queryParams.branchIds = branchIds;
    }

    if (searchQuery) {
      queryParams.query = searchQuery;
    }

    if (ids.length > 0) {
      queryParams.ids = ids;
    }

    return queryParams;
  }

  private loadFilterSources(): Observable<TimekeepingFilterSources> {
    const pageParams = (params: Record<string, string | number>, cursor?: string) =>
      _.omitBy({...params, limit: FILTER_SOURCES_PAGE_LIMIT, cursor}, _.isNil) as Record<string, string | number>;
    // A source that fails to load leaves just its filter empty.
    const withFallback = <T>(source$: Observable<T[]>, name: string) => source$.pipe(
      catchError((error) => {
        console.error(`Error loading timekeeping ${name} filter options:`, error);
        return of([] as T[]);
      })
    );

    return forkJoin({
      agentTags: withFallback(loadAllPages((cursor) => this.tagsHttpService.getAll(pageParams({type: TagType.Agent}, cursor))), 'agent tags'),
      groupTags: withFallback(loadAllPages((cursor) => this.tagsHttpService.getAll(pageParams({type: TagType.Group}, cursor))), 'group tags'),
      groups: withFallback(loadAllPages((cursor) => this.groupsHttpService.getGroups(pageParams({}, cursor))), 'groups'),
      branches: withFallback(loadAllPages((cursor) => this.branchesHttpService.query({}, pageParams({sort: 'name,asc'}, cursor))), 'branches')
    });
  }

  private groupsBranchIds(groupIds: string[]): string[] {
    const groupsById = _.keyBy(this.filterSources().groups, 'id');
    return _.uniq(_.flatMap(groupIds, (groupId) => groupsById[groupId]?.branchIds || []));
  }

  private onStickyFiltersChange() {
    this.saveStickyFilters();
    this.loadGroups(true);
  }

  private saveStickyFilters() {
    const stickyFilters = _.pick(this.state().filters, STICKY_FILTER_KEYS) as TimekeepingStickyFilters;
    this.viewFilterSettingsService.setPageFilters<TimekeepingStickyFilters>(stickyFilters, this.state().pageName);
  }

  private patchFilters(filters: Partial<TimekeepingListFilters>) {
    this.patch((state) => ({filters: {...state.filters, ...filters}}));
  }

  private patch(update: TimekeepingListStateUpdate) {
    this.state.update((state) => ({...state, ...(_.isFunction(update) ? update(state) : update)}));
  }
}
